import EmptyState from '@atlaskit/empty-state';
import Pagination from '@atlaskit/pagination';
import SectionMessage from '@atlaskit/section-message';
import TextField from '@atlaskit/textfield';
import Toggle from '@atlaskit/toggle';
import Tooltip from '@atlaskit/tooltip';
import { AfSearchIcon, AkEditorWarningIcon } from '@fuegokit/fuegoicons-react';
import { Box, Spacer } from '@fuegokit/react';
import pDebounce from 'p-debounce';
import React, {
  FormEvent,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { ErrorMessage } from '../../components/ErrorMessage';
import {
  generateRows,
  ProjectsMappingTable,
} from '../../components/ProjectsMappingTable';
import { ResetMappingsDialog } from '../../components/ResetMappingsDialog';
import { MagnifyingGlass } from '../../components/reusable/MagnifyingGlass';
import { useProjects } from '../../hooks/useGetProjects';
import { useDatabaseContext } from '../../screens/DatabaseContextProvider';
import { useTogglDataContext } from '../../screens/TogglDataProvider';
import {
  deleteAllProjectsMappings,
  deleteProjectsMappings,
  getProjectsMappings,
  setGlobalConfiguration,
  updateProjectsMappings,
} from '../../services/api';
import { getErrorMessage } from '../../services/helpers';
import { isJiraAdmin, showFlag } from '../../services/jira';
import {
  GlobalConfiguration,
  ProjectMapping,
  ProjectsMappings,
} from '../../types';

const debouncedUpdateGlobalConfiguration = pDebounce(
  async (
    update: GlobalConfiguration,
    callback: (update: GlobalConfiguration) => void
  ) => {
    callback(update);
    await setGlobalConfiguration(update);
    const message = `${
      update.mapProjectsForAll ? 'Global' : 'Personal'
    } mappings enabled`;
    showFlag(message, '', 'success');
  },
  500,
  { before: true }
);

export function ConfigProjects() {
  const [query, setQuery] = useState('');
  const { projects: togglProjects, workspaces } = useTogglDataContext();
  const [mappings, setMappings] = useState<ProjectsMappings>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const {
    data: { globalConfiguration },
    updateContext,
  } = useDatabaseContext();
  const isGlobal = globalConfiguration?.mapProjectsForAll;
  const isAdmin = isJiraAdmin();
  const isDisabled = useMemo(
    () => (isGlobal && !isAdmin) || false,
    [isGlobal, isAdmin]
  );
  const isMappingEmpty = Object.values(mappings).filter(Boolean).length === 0;
  const {
    projects: jiraProjects,
    currentPage,
    pages,
    getPage,
    loading,
  } = useProjects({
    page: 0,
    query,
    onError: () =>
      setError('There was some error while retrieving your jira projects'),
  });
  const onPageChange = useCallback(
    (event: SyntheticEvent, page: number) => {
      void getPage(page - 1);
    },
    [getPage]
  );

  const onQueryChange = useCallback((event: FormEvent<HTMLInputElement>) => {
    const target = event.target as HTMLInputElement;
    setQuery(target.value);
  }, []);

  async function getMappings(global?: boolean) {
    try {
      const mappings = await getProjectsMappings(global);
      setMappings(mappings);
    } catch (e) {
      console.error(e);
      setError('There was some error while retrieving your projects mappings');
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    setIsLoading(true);
    void getMappings(isGlobal);
  }, [isGlobal]);

  async function onProjectSelect({
    jiraProjectKey,
    togglProjectId,
  }: ProjectMapping) {
    try {
      setMappings((prev) => ({ ...prev, [jiraProjectKey]: togglProjectId }));
      if (togglProjectId) {
        await updateProjectsMappings(
          {
            jiraProjectKey,
            togglProjectId,
          },
          isGlobal
        );
      } else {
        await deleteProjectsMappings(jiraProjectKey, isGlobal);
      }

      showFlag('Success', `Mappings updated`, 'success');
    } catch (e) {
      void getMappings(isGlobal);
      const message = getErrorMessage(e);
      showFlag('Failed to update mappings', message, 'error');
    }
  }

  async function onGlobalMappingsChange(
    event: React.ChangeEvent<HTMLInputElement>
  ) {
    if (globalConfiguration) {
      const update = {
        ...globalConfiguration,
        mapProjectsForAll: event.target.checked,
      };
      try {
        const setConfiguration = updateContext('globalConfiguration');
        setConfiguration(update);
        await debouncedUpdateGlobalConfiguration(update, (update) =>
          setConfiguration(update)
        );
      } catch (e) {
        const message = `Failed to enable ${
          event.target.checked ? 'global' : 'personal'
        } mappings`;
        showFlag(message, '', 'error');
        console.error(e);
      }
    }
  }

  async function onResetMappings() {
    try {
      setIsLoading(true);
      setMappings({});
      await deleteAllProjectsMappings(isGlobal);
      showFlag('Success', `Mappings deleted`, 'success');
    } catch (e) {
      void getMappings(isGlobal);
      const message = getErrorMessage(e);
      showFlag('Failed to delete mappings', message, 'error');
    } finally {
      setIsLoading(false);
    }
  }

  const rows = useMemo(() => {
    return generateRows({
      isLoading,
      isDisabled,
      jiraProjects,
      togglProjects,
      workspaces,
      mappings,
      onProjectSelect,
    });
  }, [
    isLoading,
    jiraProjects,
    togglProjects,
    workspaces,
    mappings,
    onProjectSelect,
    isDisabled,
  ]);
  if (error) {
    return (
      <Box py={3}>
        <ErrorMessage text={error} title="Something went wrong" />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        py={3}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
      >
        <Box display="flex" alignItems="center">
          <Toggle
            isChecked={isGlobal}
            isDisabled={!isAdmin}
            onChange={(event) => {
              void onGlobalMappingsChange(event);
            }}
          />
          <Box lineHeight="14px">Enable global project mapping</Box>
          <Box color="icon.default">
            <Tooltip content="Only Jira admin can edit this options">
              <AkEditorWarningIcon />
            </Tooltip>
          </Box>
        </Box>
        <ResetMappingsDialog
          isDisabled={isDisabled || isMappingEmpty}
          onResetMappings={() => {
            void onResetMappings();
          }}
        />
      </Box>
      {isAdmin && isGlobal && (
        <Box pb={3}>
          <SectionMessage title="Global project mapping enabled">
            <p>
              Adjusting global mappings will apply changes universally to all
              users.
            </p>
          </SectionMessage>
        </Box>
      )}
      <Box maxWidth={'50%'}>
        <TextField
          elemBeforeInput={
            <Box color="icon.default">
              <AfSearchIcon />
            </Box>
          }
          value={query}
          onChange={onQueryChange}
          placeholder="Search project"
        />
      </Box>
      <Spacer axis="vertical" size={20} />
      <ProjectsMappingTable loading={loading} rows={rows} />
      {pages.length > 0 ? (
        <Box>
          <Box display="flex" justifyContent="center">
            <Pagination
              defaultSelectedIndex={0}
              selectedIndex={currentPage}
              nextLabel="Next"
              onChange={onPageChange}
              label="Page"
              pageLabel="Page"
              pages={pages}
              previousLabel="Previous"
            />
          </Box>
        </Box>
      ) : (
        !loading && (
          <EmptyState
            header="No projects were found that match your search"
            headingLevel={2}
            renderImage={() => <MagnifyingGlass />}
          />
        )
      )}
    </Box>
  );
}
